import { Autocomplete, Checkbox, TextField } from '@mui/material';
import { useMemo, useState } from 'react';
import { useFormFieldDef } from '../hooks/useFormFieldDef';

// TODO: Multiple Select still has some problems, Select all vs Select None is a little weird from UX perspective
export const DropdownSelect = ({ fieldDef, shouldDisplayCode }) => {
    const { value, setValue, onBlur, error, helperText, label, disabled } = useFormFieldDef(fieldDef);
    const options = fieldDef.values ?? [];
    const isMulti = fieldDef.multiSelect;
    const [inputValue, setInputValue] = useState('');
    const getOptionLabel = (option) => {
        if (!option) return '';
        const displayName = option.displayName ?? '';
        if (!shouldDisplayCode || option.value == null) return displayName;
        return `${option.value}: ${displayName}`;
    };

    const hasGroups = useMemo(() => options.some(option => option.group), [options]);

    const selectedValues = useMemo(
        () => (isMulti && Array.isArray(value) ? value : []),
        [isMulti, value]
    );

    const selectedOption = useMemo(() => {
        if (isMulti) return null;
        return options.find(option => option.value === value) ?? null;
    }, [isMulti, options, value]);

    const selectedOptions = useMemo(() => {
        if (!isMulti || selectedValues.length === 0) return [];
        const selectedSet = new Set(selectedValues);
        return options.filter(option => selectedSet.has(option.value));
    }, [isMulti, options, selectedValues]);

    const isAllSelected = useMemo(() => {
        if (!isMulti || options.length === 0) return false;
        const selectedSet = new Set(selectedValues);
        return options.every(option => selectedSet.has(option.value));
    }, [isMulti, options, selectedValues]);

    const allOption = useMemo(() => ({
        value: '__all__',
        displayName: isAllSelected ? 'Clear Selection' : 'Select All',
        isAllOption: true
    }), [isAllSelected]);

    const dropdownOptions = useMemo(
        () => (isMulti ? [allOption, ...options] : options),
        [allOption, isMulti, options]
    );

    const selectedSummary = useMemo(() => {
        if (!isMulti) return '';
        if (isAllSelected) return 'All';
        if (selectedOptions.length === 0) return 'None';
        if (selectedOptions.length <= 3) {
            return selectedOptions
                .map(option => option.displayName ?? String(option.value ?? ''))
                .filter(Boolean)
                .join(', ');
        }
        return 'Multiple';
    }, [isAllSelected, isMulti, selectedOptions]);

    const showSummary = isMulti && inputValue.length === 0;

    return (
        <Autocomplete
            fullWidth
            multiple={isMulti}
            disableCloseOnSelect={isMulti}
            options={dropdownOptions}
            getOptionLabel={getOptionLabel}
            groupBy={hasGroups ? (option) => (option.isAllOption ? '' : (option.group ?? '')) : undefined}
            value={isMulti ? selectedOptions : selectedOption}
            inputValue={isMulti ? inputValue : undefined}
            onInputChange={isMulti ? (_, newValue) => setInputValue(newValue) : undefined}
            onChange={(_, newValue) => {
                if (isMulti) {
                    if (newValue?.some(option => option.isAllOption)) {
                        setValue(isAllSelected ? null : options.map(option => option.value));
                        return;
                    }
                    const nextValues = newValue?.length
                        ? newValue.filter(option => !option.isAllOption).map(option => option.value)
                        : null;
                    setValue(nextValues);
                    return;
                }
                setValue(newValue ? newValue.value : undefined);
            }}
            onBlur={onBlur}
            disabled={disabled}
            disableClearable
            isOptionEqualToValue={(option, selected) => option.value === selected.value}
            renderOption={(props, option, { selected }) => (
                <li {...props}>
                    {isMulti && !option.isAllOption && (
                        <Checkbox
                            style={{ marginRight: 8 }}
                            checked={option.isAllOption ? isAllSelected : selected}
                        />
                    )}
                    {getOptionLabel(option)}
                </li>
            )}
            renderTags={isMulti ? () => null : undefined}
            renderInput={(params) => (
                <TextField
                    {...params}
                    fullWidth
                    label={label}
                    error={error}
                    helperText={helperText}
                    onBlur={onBlur}
                    variant="outlined"
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            endAdornment: params.InputProps.endAdornment,
                            startAdornment: showSummary ? (
                                <span
                                    style={{
                                        opacity: 0.7,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        pointerEvents: 'none'
                                    }}
                                >
                                    {selectedSummary}
                                </span>
                            ) : params.InputProps.startAdornment
                        }
                    }}
                />
            )}
        />
    );
};
